import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Edit2, Check, Loader2 } from 'lucide-react';
import { LineItemEditorRow } from './LineItemEditorRow';

const MAIN_COLS = [
  { key: 'part_internal_no', label: 'Part No.', type: 'text', width: 'w-24', mono: true },
  { key: 'part_description', label: 'Description', type: 'text', width: 'min-w-[180px] w-full', properCase: true },
  { key: 'hs_code', label: 'HS Code', type: 'text', width: 'w-24' },
  { key: 'net_weight_grams', label: 'Net Wt (g)', type: 'number', step: '0.001', width: 'w-20', align: 'text-right' },
  { key: 'quantity', label: 'Qty', type: 'number', step: '1', width: 'w-24', align: 'text-right', numeric: true },
  { key: 'uom', label: 'UOM', type: 'text', width: 'w-14' },
  { key: 'packing_qty', label: 'Pack Qty', type: 'number', step: '1', width: 'w-20', align: 'text-right' },
  { key: 'box_size', label: 'Box Size', type: 'text', width: 'w-28' },
  { key: 'shipment_date', label: 'Ship Date', type: 'date', width: 'w-34' },
  { key: 'unit_price', label: 'Unit Price', type: 'number', step: '0.0001', width: 'w-24', align: 'text-right', numeric: true },
  { key: 'price_unit', label: 'Per', type: 'number', step: '1', width: 'w-16', align: 'text-right' },
];

const SUB_FIELDS = [
  { key: 'customer_part_no', label: 'Cust P/N', width: 'w-28' },
  { key: 'grade', label: 'Grade', width: 'w-20' },
  { key: 'finish', label: 'Finish', width: 'w-28' },
  { key: 'manufacturing_lead_time', label: 'Mfg LT', width: 'w-24' },
  { key: 'sample_lead_time', label: 'Sample LT', width: 'w-24' },
  { key: 'tooling_cost', label: 'Tooling', type: 'number', step: '0.01', width: 'w-24' },
  { key: 'sample_cost', label: 'Sample Cost', type: 'number', step: '0.01', width: 'w-24' },
  { key: 'remarks', label: 'Remarks', width: 'flex-1 min-w-[140px]' },
];

const lineTotal = (item) => {
  const pu = parseFloat(item.price_unit) || 1;
  return ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)) / (pu || 1);
};

export function EditOrderModal({ 
  open, onOpenChange, order, editForm, setEditForm,
  editItems, updateEditItem, onSave, saving, formatCurrency 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[96vw] xl:max-w-[1600px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 className="h-5 w-5 text-blue-600" />
            Edit / Amend Sales Confirmation
          </DialogTitle>
          <DialogDescription>
            Amend {order.order_number} to match revised PO details. This updates the existing record.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Customer PO Number</Label>
              <Input
                value={editForm.customer_po_number}
                onChange={(e) => setEditForm({ ...editForm, customer_po_number: e.target.value })}
                placeholder="Enter PO number"
                data-testid="edit-po-number"
              />
            </div>
            <div>
              <Label>Customer PO Date</Label>
              <Input
                type="date"
                value={editForm.customer_po_date}
                onChange={(e) => setEditForm({ ...editForm, customer_po_date: e.target.value })}
                data-testid="edit-po-date"
              />
            </div>
            <div>
              <Label>Delivery Date</Label>
              <Input
                type="date"
                value={editForm.delivery_date}
                onChange={(e) => setEditForm({ ...editForm, delivery_date: e.target.value })}
                data-testid="edit-delivery-date"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Amendment notes"
              />
            </div>
          </div>

          <div>
            <Label>Ship To Address</Label>
            <Textarea
              value={editForm.ship_to_address || ''}
              onChange={(e) => setEditForm({ ...editForm, ship_to_address: e.target.value })}
              placeholder="Delivery / shipping address (leave blank to use customer billing address)"
              rows={2}
              data-testid="edit-ship-to-address"
            />
          </div>

          <div>
            <Label className="text-base font-semibold">Line Items</Label>
            <div className="mt-2 border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wide">
                    <th className="px-2 py-2 text-left w-8">#</th>
                    {MAIN_COLS.map(c => (
                      <th key={c.key} className={`px-1.5 py-2 ${c.align === 'text-right' ? 'text-right' : 'text-left'}`}>{c.label}</th>
                    ))}
                    <th className="px-2 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {editItems.map((item, idx) => (
                    <LineItemEditorRow
                      key={item.id || `edit-${idx}`}
                      item={item}
                      idx={idx}
                      mainCols={MAIN_COLS}
                      subFields={SUB_FIELDS}
                      updateEditItem={updateEditItem}
                      formatCurrency={formatCurrency}
                      lineTotal={lineTotal}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="flex justify-end mt-3 p-3 bg-slate-50 rounded-lg">
              <div className="text-right">
                <span className="text-sm text-slate-500">Revised Total: </span>
                <span className="text-lg font-bold text-blue-600">
                  {formatCurrency(editItems.reduce((sum, item) => sum + lineTotal(item), 0))}
                </span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-amendment-btn"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            {saving ? 'Saving...' : 'Save Amendment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
